import { useEffect, useState } from "react";

import {
  DASHBOARD_CHART_DIMENSIONS,
  DEFAULT_CONTRIBUTE_AFTER_18,
  DEFAULT_MULTIPLIER,
  DEFAULT_PROJECTED_AGE,
  DEFAULT_RECURRING_AMOUNT,
} from "@constants";
import { Flow, SipFrequency } from "@ctypes";
import { useGoalsContext, useTheme } from "@hooks";
import { constants } from "@layout";
import {
  buildBarChart,
  calculateInvestedProgression,
  calculateProjectedProgression,
  calculateTotalInviteAmountInCents,
  calculateTotalPartnerAmountInCents,
  convertCentsToDollar,
  getAvailableFrequencies,
  getMinDepositAmount,
} from "@utils";
import dayjs from "dayjs";
import { ProjectedChartContext } from "./context";
import { ProjectedChartProviderProps } from "./types";

const CHART_SIP_FREQUENCIES = [SipFrequency.Weekly, SipFrequency.Monthly, SipFrequency.Annually];

export const ProjectedChartProvider = ({
  accountType,
  currentAge: currentAgeProp = 0,
  children,
  recurringAmount: customRecurringAmount = DEFAULT_RECURRING_AMOUNT,
  frequency: customFrequencyProp = SipFrequency.Weekly,
  initialBalance: initialBalanceProp = 0,
  investedColor: investedColorProp,
  returnedColor: returnedColorProp,
  projectedAge: projectedAgeProp,
  multiplier: multiplierProp,
  contributeAfter18: contributeProp,
  width = DASHBOARD_CHART_DIMENSIONS,
  height = DASHBOARD_CHART_DIMENSIONS,
  barChartWidth = constants.chart.projection.width,
  barChartHeight = constants.chart.projection.height,
  invertedTheme = false,
  showInvestmentAmounts = true,
  flow = Flow.Default,
  pendingInvite,
}: ProjectedChartProviderProps) => {
  const availableFrequencies = getAvailableFrequencies(CHART_SIP_FREQUENCIES, accountType);

  const customFrequency = availableFrequencies.includes(customFrequencyProp)
    ? customFrequencyProp
    : availableFrequencies[0]!;

  const minDeposit = getMinDepositAmount(accountType);
  const effectiveRecurringAmount =
    customRecurringAmount < minDeposit && flow !== Flow.EditGoal
      ? minDeposit
      : customRecurringAmount;

  const { theme } = useTheme();

  const { goal } = useGoalsContext();

  const investedColor = investedColorProp ?? theme.chart.projection.default.invested;
  const returnedColor = returnedColorProp ?? theme.chart.projection.default.returned;

  const defaultMultiplier = multiplierProp ?? DEFAULT_MULTIPLIER;
  const defaultContributeAfter18 = contributeProp ?? DEFAULT_CONTRIBUTE_AFTER_18;
  const defaultProjectedAge = projectedAgeProp ?? DEFAULT_PROJECTED_AGE;

  const [currentAge, setCurrentAge] = useState(currentAgeProp);
  const [projectedAge, setProjectedAge] = useState(defaultProjectedAge);
  const [multiplier, setMultiplier] = useState(defaultMultiplier);
  const [contributeAfter18, setContributeAfter18] = useState(defaultContributeAfter18);
  const [initialBalance, setInitialBalance] = useState(initialBalanceProp);

  const [recurringAmount, setRecurringAmount] = useState(effectiveRecurringAmount);
  const [recurringDepositType, setRecurringDepositType] = useState<SipFrequency>(customFrequency);
  const [isInfoVisible, setIsInfoVisible] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- sync props to local state */
  useEffect(() => {
    setCurrentAge(currentAgeProp);
    setProjectedAge(defaultProjectedAge);
    setMultiplier(defaultMultiplier);
    setContributeAfter18(defaultContributeAfter18);
    setInitialBalance(initialBalanceProp);
    setRecurringAmount(effectiveRecurringAmount);
    setRecurringDepositType(customFrequency);
  }, [
    currentAgeProp,
    defaultProjectedAge,
    defaultMultiplier,
    defaultContributeAfter18,
    initialBalanceProp,
    effectiveRecurringAmount,
    customFrequency,
  ]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const interest = Math.round((multiplier - 1) * 100);

  const isDirty =
    multiplier !== defaultMultiplier ||
    contributeAfter18 !== defaultContributeAfter18 ||
    projectedAge !== defaultProjectedAge ||
    recurringAmount !== effectiveRecurringAmount ||
    recurringDepositType !== customFrequency;

  const adjustedRecurringAmount = (() => {
    if (flow === Flow.AcceptSharedGoal && pendingInvite?.recurringDepositInCents) {
      const partnerAmountInCents = calculateTotalInviteAmountInCents(pendingInvite);

      return recurringAmount + convertCentsToDollar(partnerAmountInCents);
    }

    if (flow === Flow.EditGoal) {
      const partnerAmountInCents = calculateTotalPartnerAmountInCents({ goal });

      return recurringAmount + convertCentsToDollar(partnerAmountInCents);
    }

    return recurringAmount;
  })();

  const safeProjectedAge = Math.max(projectedAge, currentAge);

  const invested = calculateInvestedProgression({
    amount: adjustedRecurringAmount,
    recurringDepositType,
    currentAge,
    projectedAge: safeProjectedAge,
    contributeAfter18,
    initialBalance,
  });

  const projected = calculateProjectedProgression({
    amount: adjustedRecurringAmount,
    recurringDepositType,
    currentAge,
    projectedAge: safeProjectedAge,
    multiplier,
    contributeAfter18,
    initialBalance,
  });

  const targetYear = safeProjectedAge - currentAge + dayjs().year();

  const investedAmount = invested.at(-1)?.amount ?? 0;
  const projectedAmount = projected.at(-1)?.amount ?? 0;

  const returnedAmount = projectedAmount - investedAmount;

  const [x, y] = buildBarChart({
    invested,
    projected,
    width: barChartWidth,
  });

  const props = {
    value: {
      accountType,
      recurringAmount,
      adjustedRecurringAmount,
      setRecurringAmount,
      invested,
      projected,
      currentAge,
      setCurrentAge,
      projectedAge,
      setProjectedAge,
      x,
      y,
      recurringDepositType,
      setRecurringDepositType,
      multiplier,
      setMultiplier,
      isInfoVisible,
      setIsInfoVisible,
      contributeAfter18,
      setContributeAfter18,
      initialBalance,
      setInitialBalance,
      investedColor,
      returnedColor,
      targetYear,
      investedAmount,
      projectedAmount,
      returnedAmount,
      interest,
      height,
      width,
      barChartWidth,
      barChartHeight,
      invertedTheme,
      showInvestmentAmounts,
      isDirty,
    },
  };
  return <ProjectedChartContext.Provider {...props}>{children}</ProjectedChartContext.Provider>;
};
